// @flow

import type { UserRegistrationState, UserRegistrationActions, ApiState } from '../types';
import {
  WS_INIT_REGISTRATION,
  WS_INIT_REGISTRATION_SUCCESS,
  WS_GET_RECAPTCHA,
  WS_GET_RECAPTCHA_SUCCESS,
  WS_CREATE_USER_REGISTRATION_REQUEST,
  WS_CREATE_USER_REGISTRATION_SUCCESS,
  WS_CREATE_USER_REGISTRATION_ERROR,
  WS_GET_USER_REGISTRATION_REQUEST,
  WS_GET_USER_REGISTRATION_SUCCESS,
  WS_GET_USER_REGISTRATION_ERROR,
  WS_SEND_USER_REGISTRATION_SMS_CODE_REQUEST,
  WS_SEND_USER_REGISTRATION_SMS_CODE_SUCCESS,
  WS_SEND_USER_REGISTRATION_SMS_CODE_ERROR,
  WS_VALIDATE_USER_REGISTRATION_SMS_CODE_REQUEST,
  WS_VALIDATE_USER_REGISTRATION_SMS_CODE_SUCCESS,
  WS_VALIDATE_USER_REGISTRATION_SMS_CODE_ERROR,
  WS_UPDATE_USER_REGISTRATION_REQUEST,
  WS_UPDATE_USER_REGISTRATION_SUCCESS,
  WS_UPDATE_USER_REGISTRATION_ERROR,
  WS_UPLOAD_USER_REGISTRATION_KYC_REQUEST,
  WS_UPLOAD_USER_REGISTRATION_KYC_SUCCESS,
  WS_UPLOAD_USER_REGISTRATION_KYC_ERROR,
  WS_SCAN_USER_REGISTRATION_KYC_REQUEST,
  WS_SCAN_USER_REGISTRATION_KYC_SUCCESS,
  WS_SCAN_USER_REGISTRATION_KYC_ERROR,
  WS_SUGGEST_NICKNAME_REQUEST,
  WS_SUGGEST_NICKNAME_SUCCESS,
  WS_SUGGEST_NICKNAME_ERROR,
  WS_IS_AVAILABLE_NICKNAME_REQUEST,
  WS_IS_AVAILABLE_NICKNAME_SUCCESS,
  WS_IS_AVAILABLE_NICKNAME_ERROR,
  WS_VALIDATE_USER_REGISTRATION_DOMICILIATION_CODE_REQUEST,
  WS_VALIDATE_USER_REGISTRATION_DOMICILIATION_CODE_SUCCESS,
  WS_VALIDATE_USER_REGISTRATION_DOMICILIATION_CODE_ERROR,
  WS_SEND_USER_REGISTRATION_VALIDATION_CODE_REQUEST,
  WS_SEND_USER_REGISTRATION_VALIDATION_CODE_SUCCESS,
  WS_SEND_USER_REGISTRATION_VALIDATION_CODE_ERROR,
  WS_CLEAR_USER_REGISTRATION_RESPONSES,
  WS_LIST_INTERNATIONAL_COUNTRY_CALLING_CODES_REQUEST,
  WS_LIST_INTERNATIONAL_COUNTRY_CALLING_CODES_SUCCESS,
  WS_LIST_INTERNATIONAL_COUNTRY_CALLING_CODES_ERROR,
  WS_SEARCH_PLACES_REQUEST,
  WS_SEARCH_PLACES_SUCCESS,
  WS_CLEAR_SEARCH_PLACES_RESULTS,
  WS_SEARCH_PLACES_ERROR,
  WS_DISCONNECTED_REGISTRATION,
  WS_GET_PLACE_DETAILS_REQUEST,
  WS_GET_PLACE_DETAILS_SUCCESS,
  WS_GET_PLACE_DETAILS_ERROR,
  WS_CLEAR_GET_PLACE_DETAILS_RESULTS,
  GET_CAPTCHA,
  SET_CAPTCHA,
} from '../actions';

const requestInitialState = (): ApiState<any, any, any> => ({
  loading: false,
  time: null,
  success: false,
  params: null,
  error: null,
  response: null,
});

export const initialState: UserRegistrationState = {
  initializing: false,
  initialized: false,
  recaptching: false,
  recaptcha: '',
  recaptchaExec: null,
  captcha: {
    token: '',
    fetching: false,
  },
  user: null,
  createUserRegistration: requestInitialState(),
  getUserRegistration: requestInitialState(),
  sendUserRegistrationSmsCode: requestInitialState(),
  validateUserRegistrationSmsCode: requestInitialState(),
  updateUserRegistration: requestInitialState(),
  uploadUserRegistrationKYC: requestInitialState(),
  scanUserRegistrationKYC: requestInitialState(),
  suggestNickname: requestInitialState(),
  isAvailableNickname: requestInitialState(),
  validateUserRegistrationDomiciliationCode: requestInitialState(),
  sendUserRegistrationValidationCode: requestInitialState(),
  listInternationalCountryCallingCodes: requestInitialState(),
  searchPlaces: requestInitialState(),
  getPlaceDetails: requestInitialState(),
};

export const userRegistrationReducer = (
  state: UserRegistrationState = initialState,
  action: UserRegistrationActions
): UserRegistrationState => {
  switch (action.type) {
    case WS_INIT_REGISTRATION:
      return {
        ...state,
        initializing: true,
        initialized: false,
      };

    case WS_INIT_REGISTRATION_SUCCESS:
      return {
        ...state,
        initializing: false,
        initialized: true,
        recaptcha: '',
      };

    case WS_DISCONNECTED_REGISTRATION:
      return {
        ...state,
        initializing: false,
        initialized: false,
      };

    case WS_GET_RECAPTCHA:
      return {
        ...state,
        recaptching: true,
        recaptcha: '',
        recaptchaExec: action.payload || state.recaptchaExec,
      };

    case WS_GET_RECAPTCHA_SUCCESS:
      return {
        ...state,
        recaptching: false,
        recaptcha: action.payload,
      };

    case GET_CAPTCHA:
      return {
        ...state,
        captcha: {
          token: '',
          fetching: true,
        },
      };

    case SET_CAPTCHA:
      return {
        ...state,
        captcha: {
          token: action.payload,
          fetching: false,
        },
      };

    case WS_CREATE_USER_REGISTRATION_REQUEST:
      return {
        ...state,
        createUserRegistration: {
          loading: true,
          time: Date.now(),
          success: false,
          params: action.payload,
          error: null,
          response: null,
        },
      };

    case WS_CREATE_USER_REGISTRATION_SUCCESS:
      return {
        ...state,
        createUserRegistration: {
          ...state.createUserRegistration,
          loading: false,
          time: null,
          success: true,
          error: null,
          response: action.payload,
        },
      };

    case WS_CREATE_USER_REGISTRATION_ERROR:
      return {
        ...state,
        createUserRegistration: {
          ...state.createUserRegistration,
          loading: false,
          time: null,
          success: false,
          error: action.payload,
          response: null,
        },
      };

    case WS_GET_USER_REGISTRATION_REQUEST:
      return {
        ...state,
        getUserRegistration: {
          loading: true,
          time: Date.now(),
          success: false,
          params: action.payload,
          error: null,
          response: null,
        },
      };

    case WS_GET_USER_REGISTRATION_SUCCESS:
      const { identity_scan, ...payloadWithoutScanInfo } = action.payload;
      return {
        ...state,
        getUserRegistration: {
          ...state.getUserRegistration,
          loading: false,
          time: null,
          success: true,
          error: null,
          response: payloadWithoutScanInfo,
        },
        user: payloadWithoutScanInfo,
      };

    case WS_GET_USER_REGISTRATION_ERROR:
      return {
        ...state,
        getUserRegistration: {
          ...state.getUserRegistration,
          loading: false,
          time: null,
          success: false,
          error: action.payload,
          response: null,
        },
      };

    case WS_SEND_USER_REGISTRATION_SMS_CODE_REQUEST:
      return {
        ...state,
        sendUserRegistrationSmsCode: {
          loading: true,
          time: Date.now(),
          success: false,
          params: action.payload,
          error: null,
          response: null,
        },
      };

    case WS_SEND_USER_REGISTRATION_SMS_CODE_SUCCESS:
      return {
        ...state,
        sendUserRegistrationSmsCode: {
          ...state.sendUserRegistrationSmsCode,
          loading: false,
          time: null,
          success: true,
          error: null,
          response: action.payload,
        },
      };

    case WS_SEND_USER_REGISTRATION_SMS_CODE_ERROR:
      return {
        ...state,
        sendUserRegistrationSmsCode: {
          ...state.sendUserRegistrationSmsCode,
          loading: false,
          time: null,
          success: false,
          error: action.payload,
          response: null,
        },
      };

    case WS_VALIDATE_USER_REGISTRATION_SMS_CODE_REQUEST:
      return {
        ...state,
        validateUserRegistrationSmsCode: {
          loading: true,
          time: Date.now(),
          success: false,
          params: action.payload,
          error: null,
          response: null,
        },
      };

    case WS_VALIDATE_USER_REGISTRATION_SMS_CODE_SUCCESS:
      return {
        ...state,
        validateUserRegistrationSmsCode: {
          ...state.validateUserRegistrationSmsCode,
          loading: false,
          time: null,
          success: true,
          error: null,
          response: action.payload,
        },
      };

    case WS_VALIDATE_USER_REGISTRATION_SMS_CODE_ERROR:
      return {
        ...state,
        validateUserRegistrationSmsCode: {
          ...state.validateUserRegistrationSmsCode,
          loading: false,
          time: null,
          success: false,
          error: action.payload,
          response: null,
        },
      };

    case WS_UPDATE_USER_REGISTRATION_REQUEST:
      return {
        ...state,
        updateUserRegistration: {
          loading: true,
          time: Date.now(),
          success: false,
          params: action.payload,
          error: null,
          response: null,
        },
      };

    case WS_UPDATE_USER_REGISTRATION_SUCCESS:
      return {
        ...state,
        updateUserRegistration: {
          ...state.updateUserRegistration,
          loading: false,
          time: null,
          success: true,
          error: null,
          response: action.payload,
        },
      };

    case WS_UPDATE_USER_REGISTRATION_ERROR:
      return {
        ...state,
        updateUserRegistration: {
          ...state.updateUserRegistration,
          loading: false,
          time: null,
          success: false,
          error: action.payload,
          response: null,
        },
      };

    case WS_UPLOAD_USER_REGISTRATION_KYC_REQUEST:
      return {
        ...state,
        uploadUserRegistrationKYC: {
          loading: true,
          time: Date.now(),
          success: false,
          params: action.payload,
          error: null,
          response: null,
        },
      };

    case WS_UPLOAD_USER_REGISTRATION_KYC_SUCCESS:
      const { type } = action.payload.data;
      const user = {
        ...state.user,
        proof_of_identity_uploaded:
          (state.user && state.user.proof_of_identity_uploaded) || type === 'identity',
        proof_of_address_uploaded:
          (state.user && state.user.proof_of_address_uploaded) || type === 'residence',
      };
      return {
        ...state,
        uploadUserRegistrationKYC: {
          ...state.uploadUserRegistrationKYC,
          loading: false,
          time: null,
          success: true,
          error: null,
          response: action.payload,
        },
        user,
      };

    case WS_UPLOAD_USER_REGISTRATION_KYC_ERROR:
      return {
        ...state,
        uploadUserRegistrationKYC: {
          ...state.uploadUserRegistrationKYC,
          loading: false,
          time: null,
          success: false,
          error: action.payload,
          response: null,
        },
      };

    case WS_SCAN_USER_REGISTRATION_KYC_REQUEST:
      return {
        ...state,
        scanUserRegistrationKYC: {
          loading: true,
          time: Date.now(),
          success: false,
          params: action.payload,
          error: null,
          response: null,
        },
      };

    case WS_SCAN_USER_REGISTRATION_KYC_SUCCESS:
      return {
        ...state,
        scanUserRegistrationKYC: {
          ...state.scanUserRegistrationKYC,
          loading: false,
          time: null,
          success: true,
          error: null,
          response: action.payload,
        },
      };

    case WS_SCAN_USER_REGISTRATION_KYC_ERROR:
      return {
        ...state,
        scanUserRegistrationKYC: {
          ...state.scanUserRegistrationKYC,
          loading: false,
          time: null,
          success: false,
          error: action.payload,
          response: null,
        },
      };

    case WS_SUGGEST_NICKNAME_REQUEST:
      return {
        ...state,
        suggestNickname: {
          loading: true,
          time: Date.now(),
          success: false,
          params: action.payload,
          error: null,
          response: null,
        },
      };

    case WS_SUGGEST_NICKNAME_SUCCESS:
      return {
        ...state,
        suggestNickname: {
          ...state.suggestNickname,
          loading: false,
          time: null,
          success: true,
          error: null,
          response: action.payload,
        },
      };

    case WS_SUGGEST_NICKNAME_ERROR:
      return {
        ...state,
        suggestNickname: {
          ...state.suggestNickname,
          loading: false,
          time: null,
          success: false,
          error: action.payload,
          response: null,
        },
      };

    case WS_IS_AVAILABLE_NICKNAME_REQUEST:
      return {
        ...state,
        isAvailableNickname: {
          loading: true,
          time: Date.now(),
          success: false,
          params: action.payload,
          error: null,
          response: null,
        },
      };

    case WS_IS_AVAILABLE_NICKNAME_SUCCESS:
      return {
        ...state,
        isAvailableNickname: {
          ...state.isAvailableNickname,
          loading: false,
          time: null,
          success: true,
          error: null,
          response: action.payload,
        },
      };

    case WS_IS_AVAILABLE_NICKNAME_ERROR:
      return {
        ...state,
        isAvailableNickname: {
          ...state.isAvailableNickname,
          loading: false,
          time: null,
          success: false,
          error: action.payload,
          response: null,
        },
      };

    case WS_VALIDATE_USER_REGISTRATION_DOMICILIATION_CODE_REQUEST:
      return {
        ...state,
        validateUserRegistrationDomiciliationCode: {
          loading: true,
          time: Date.now(),
          success: false,
          params: action.payload,
          error: null,
          response: null,
        },
      };

    case WS_VALIDATE_USER_REGISTRATION_DOMICILIATION_CODE_SUCCESS:
      return {
        ...state,
        validateUserRegistrationDomiciliationCode: {
          ...state.validateUserRegistrationDomiciliationCode,
          loading: false,
          time: null,
          success: true,
          error: null,
          response: action.payload,
        },
      };

    case WS_VALIDATE_USER_REGISTRATION_DOMICILIATION_CODE_ERROR:
      return {
        ...state,
        validateUserRegistrationDomiciliationCode: {
          ...state.validateUserRegistrationDomiciliationCode,
          loading: false,
          time: null,
          success: false,
          error: action.payload,
          response: null,
        },
      };

    case WS_SEND_USER_REGISTRATION_VALIDATION_CODE_REQUEST:
      return {
        ...state,
        sendUserRegistrationValidationCode: {
          loading: true,
          time: Date.now(),
          success: false,
          params: action.payload,
          error: null,
          response: null,
        },
      };

    case WS_SEND_USER_REGISTRATION_VALIDATION_CODE_SUCCESS:
      return {
        ...state,
        sendUserRegistrationValidationCode: {
          ...state.sendUserRegistrationValidationCode,
          loading: false,
          time: null,
          success: true,
          error: null,
          response: action.payload,
        },
      };

    case WS_SEND_USER_REGISTRATION_VALIDATION_CODE_ERROR:
      return {
        ...state,
        sendUserRegistrationValidationCode: {
          ...state.sendUserRegistrationValidationCode,
          loading: false,
          time: null,
          success: false,
          error: action.payload,
          response: null,
        },
      };

    case WS_LIST_INTERNATIONAL_COUNTRY_CALLING_CODES_REQUEST:
      return {
        ...state,
        listInternationalCountryCallingCodes: {
          loading: true,
          time: Date.now(),
          success: false,
          params: action.payload,
          error: null,
          response: null,
        },
      };

    case WS_SEARCH_PLACES_REQUEST:
      return {
        ...state,
        searchPlaces: {
          loading: true,
          time: Date.now(),
          success: false,
          params: action.payload,
          error: null,
          response: null,
        },
      };

    case WS_SEARCH_PLACES_ERROR:
      return {
        ...state,
        searchPlaces: {
          ...state.searchPlaces,
          loading: false,
          time: null,
          success: false,
          error: action.payload,
          response: null,
        },
      };

    case WS_SEARCH_PLACES_SUCCESS:
      return {
        ...state,
        searchPlaces: {
          ...state.searchPlaces,
          loading: false,
          time: null,
          success: true,
          error: null,
          response: action.payload,
        },
      };

    case WS_CLEAR_SEARCH_PLACES_RESULTS:
      return {
        ...state,
        searchPlaces: requestInitialState(),
      };

    case WS_GET_PLACE_DETAILS_REQUEST:
      return {
        ...state,
        getPlaceDetails: {
          loading: true,
          time: Date.now(),
          success: false,
          params: action.payload,
          error: null,
          response: null,
        },
      };

    case WS_GET_PLACE_DETAILS_ERROR:
      return {
        ...state,
        getPlaceDetails: {
          ...state.getPlaceDetails,
          loading: false,
          time: null,
          success: false,
          error: action.payload,
          response: null,
        },
      };

    case WS_GET_PLACE_DETAILS_SUCCESS:
      return {
        ...state,
        getPlaceDetails: {
          ...state.getPlaceDetails,
          loading: false,
          time: null,
          success: true,
          error: null,
          response: action.payload,
        },
      };

    case WS_CLEAR_GET_PLACE_DETAILS_RESULTS:
      return {
        ...state,
        getPlaceDetails: requestInitialState(),
      };

    case WS_LIST_INTERNATIONAL_COUNTRY_CALLING_CODES_SUCCESS:
      return {
        ...state,
        listInternationalCountryCallingCodes: {
          ...state.listInternationalCountryCallingCodes,
          loading: false,
          time: null,
          success: true,
          error: null,
          response: action.payload,
        },
      };

    case WS_LIST_INTERNATIONAL_COUNTRY_CALLING_CODES_ERROR:
      return {
        ...state,
        listInternationalCountryCallingCodes: {
          ...state.listInternationalCountryCallingCodes,
          loading: false,
          time: null,
          success: false,
          error: action.payload,
          response: null,
        },
      };

    case WS_CLEAR_USER_REGISTRATION_RESPONSES:
      return {
        ...state,
        createUserRegistration: requestInitialState(),
        getUserRegistration: requestInitialState(),
        sendUserRegistrationSmsCode: requestInitialState(),
        validateUserRegistrationSmsCode: requestInitialState(),
        updateUserRegistration: requestInitialState(),
        uploadUserRegistrationKYC: requestInitialState(),
        scanUserRegistrationKYC: requestInitialState(),
        suggestNickname: requestInitialState(),
        isAvailableNickname: requestInitialState(),
        validateUserRegistrationDomiciliationCode: requestInitialState(),
        sendUserRegistrationValidationCode: requestInitialState(),
        listInternationalCountryCallingCodes: requestInitialState(),
      };
    default:
      return state;
  }
};
